import { assignGenerations } from './treeLayout.js';

export const FAMILY_COLORS = [
  '#1540dc', '#ff9600', '#09ad01', '#ff0000', '#e926e7', '#17e06c',
  '#00bcd4', '#FFD600', '#7B00FF',
];

export const SYMBOLS = [
  'square', 'circle', 'triangle', 'pentagon', 'diamond',
  'rectangle', 'trapezoid', 'parallelogram',
];

/**
 * Bestimmt, welche Generation welche Formatierung bekommt.
 * Oberste Gen = Gen0 (Mehrfarben-Rahmen), dann Gen1 (Farben), Gen2 (Symbole), Gen3 (Outline).
 */
export function getColorLevels(persons, relationships, genMap = null) {
  if (!persons.length) return null;
  const gm = genMap || assignGenerations(persons, relationships);
  const byGen = new Map();
  gm.forEach((g, id) => {
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(id);
  });
  const sortedGens = [...byGen.keys()].sort((a, b) => a - b);
  if (sortedGens.length < 2) return null;
  const top = byGen.get(sortedGens[0]) || [];
  const parentsOf = new Map();
  relationships.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!parentsOf.has(r.to)) parentsOf.set(r.to, []);
    parentsOf.get(r.to).push(r.from);
  });
  const gen0    = new Set(top);
  const gen1ids = byGen.get(sortedGens[1]) || [];
  return {
    gen0,
    gen1ids,
    gen2ids: byGen.get(sortedGens[2]) || [],
    gen3ids: byGen.get(sortedGens[3]) || [],
    gen1siblings: gen1ids.filter(id => (parentsOf.get(id) || []).some(p => gen0.has(p))),
  };
}

export function computeColorSystem(persons, relationships, customColors = {}) {
  if (!persons.length) return new Map();

  const genMap     = assignGenerations(persons, relationships);
  const parentsOf  = new Map();
  const childrenOf = new Map();
  const currentPartnerOf = new Map();
  const exPartnerOf      = new Map();

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!parentsOf.has(r.to))    parentsOf.set(r.to, []);
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      parentsOf.get(r.to).push(r.from);
      childrenOf.get(r.from).push(r.to);
    } else if (r.type === 'partner') {
      if (!currentPartnerOf.has(r.from)) currentPartnerOf.set(r.from, []);
      if (!currentPartnerOf.has(r.to))   currentPartnerOf.set(r.to, []);
      currentPartnerOf.get(r.from).push(r.to);
      currentPartnerOf.get(r.to).push(r.from);
    } else if (r.type === 'ex-partner') {
      if (!exPartnerOf.has(r.from)) exPartnerOf.set(r.from, []);
      if (!exPartnerOf.has(r.to))   exPartnerOf.set(r.to, []);
      exPartnerOf.get(r.from).push(r.to);
      exPartnerOf.get(r.to).push(r.from);
    }
  });

  const allPartnerOf = (id) => [
    ...(currentPartnerOf.get(id) || []),
    ...(exPartnerOf.get(id) || []),
  ];

  const personById = new Map(persons.map(p => [p.id, p]));
  const levels = getColorLevels(persons, relationships, genMap);
  if (!levels) return new Map();
  const { gen0, gen1ids, gen2ids, gen3ids, gen1siblings } = levels;
  const gen1set = new Set(gen1ids);
  const gen2set = new Set(gen2ids);
  const gen3set = new Set(gen3ids);

  // ── FARBEN ────────────────────────────────────────────────────────────
  const colorMap = new Map();
  gen1siblings.forEach((id, i) => {
    const custom = customColors[id];
    const ci = (custom !== undefined && custom >= 0 && custom < FAMILY_COLORS.length) ? custom : i % FAMILY_COLORS.length;
    colorMap.set(id, ci);
  });

  // Wenn zwei gen1-Geschwister (beide mit Eltern in gen0) Partner sind,
  // bekommen sie die gleiche Farbe — niedrigerer Farbindex gewinnt.
  const gen1sibSet = new Set(gen1siblings);
  let reconChanged = true;
  while (reconChanged) {
    reconChanged = false;
    gen1siblings.forEach(id => {
      const ci = colorMap.get(id);
      allPartnerOf(id).filter(pid => gen1sibSet.has(pid)).forEach(pid => {
        const pci = colorMap.get(pid);
        if (pci === undefined || pci === ci) return;
        if (ci < pci) {
          colorMap.set(pid, ci);
        } else {
          colorMap.set(id, pci);
        }
        reconChanged = true;
      });
    });
  }

  gen1siblings.forEach(id => {
    const ci = colorMap.get(id);
    allPartnerOf(id).forEach(pid => { if (!colorMap.has(pid)) colorMap.set(pid, ci); });
  });

  // BFS: gen1siblings + ihre Partner als Startpunkte
  const bfsStart = [...gen1siblings];
  gen1siblings.forEach(id => allPartnerOf(id).forEach(pid => { if (!bfsStart.includes(pid)) bfsStart.push(pid); }));
  const bfsQ = [...bfsStart], bfsV = new Set(bfsStart);
  while (bfsQ.length) {
    const cur = bfsQ.shift(), ci = colorMap.get(cur);
    (childrenOf.get(cur) || []).forEach(child => {
      if (!colorMap.has(child)) colorMap.set(child, ci);
      allPartnerOf(child).forEach(p => { if (!colorMap.has(p)) colorMap.set(p, ci); });
      if (!bfsV.has(child)) { bfsV.add(child); bfsQ.push(child); }
    });
  }

  // ── MULTI-RAHMEN für Gen0 ─────────────────────────────────────────────
  const borderColorsMap = new Map();
  gen0.forEach(id => {
    const cols = [...new Set(
      (childrenOf.get(id) || []).map(c => colorMap.get(c)).filter(c => c !== undefined)
    )];
    if (cols.length) borderColorsMap.set(id, cols);
  });

  // ── SYMBOLE für Gen1 ─────────────────────────────────────────────────
  // Wird NACH gen2symMap berechnet (siehe unten), damit die echten Gen2-Symbole
  // der Kinder verwendet werden können. Hier nur Deklaration.
  const symbolMap = new Map(); // gen1 personId -> symIdx[]

  // ── SYMBOLE für Gen2 ──────────────────────────────────────────────────
  // Gen2-Geschwister (direkte Kinder von Gen1) bekommen Symbole nach Geburtsreihenfolge
  // pro Farbe neu beginnend bei Quadrat.
  // Expartner von Gen1 bekommen das nächste verfügbare Symbol nach allen Geschwistern.
  const gen2symMap = new Map(); // personId -> symIdx (number) or symIdx[] (array)

  // Schritt 1: Symbole an Gen2-Geschwister vergeben
  const colorToGen2sibs = new Map(); // colorIdx -> [personId]
  gen2ids.forEach(id => {
    const hasGen1Parent = (parentsOf.get(id) || []).some(p => gen1set.has(p));
    if (!hasGen1Parent) return;
    const ci = colorMap.get(id);
    if (ci === undefined) return;
    if (!colorToGen2sibs.has(ci)) colorToGen2sibs.set(ci, []);
    colorToGen2sibs.get(ci).push(id);
  });

  colorToGen2sibs.forEach((ids, ci) => {
    ids.sort((a, b) => {
      const da = personById.get(a)?.birthDate || '';
      const db = personById.get(b)?.birthDate || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });
    ids.forEach((id, i) => gen2symMap.set(id, i % SYMBOLS.length));
  });

  // Schritt 2: Aktuelle Partner der Gen2-Geschwister erben Symbol
  gen2symMap.forEach((symIdx, sibId) => {
    if (!gen2set.has(sibId)) return;
    (currentPartnerOf.get(sibId) || []).forEach(pid => {
      if (!gen2symMap.has(pid)) gen2symMap.set(pid, symIdx);
    });
  });

  // Schritt 3: Expartner von Gen2-Geschwistern bekommen nächstes Symbol der Familie
  const colorNextSym = new Map();
  colorToGen2sibs.forEach((ids, ci) => {
    colorNextSym.set(ci, ids.length); // startet nach den Geschwistern
  });

  // Sortiere Gen2-Geschwister nach Geburtsdatum damit Expartner-Reihenfolge deterministisch ist
  gen2ids.forEach(sibId => {
    const hasGen1Parent = (parentsOf.get(sibId) || []).some(p => gen1set.has(p));
    if (!hasGen1Parent) return;
    const ci = colorMap.get(sibId);
    if (ci === undefined) return;
    (exPartnerOf.get(sibId) || []).forEach(exId => {
      if (gen2symMap.has(exId)) return;
      const next = colorNextSym.get(ci) ?? 0;
      gen2symMap.set(exId, next % SYMBOLS.length);
      colorNextSym.set(ci, next + 1);
    });
  });

  // Schritt 4: Gen2-Geschwister bekommen zusätzlich das Symbol ihrer Expartner
  gen2ids.forEach(sibId => {
    const hasGen1Parent = (parentsOf.get(sibId) || []).some(p => gen1set.has(p));
    if (!hasGen1Parent) return;
    (exPartnerOf.get(sibId) || []).forEach(exId => {
      const exSym = gen2symMap.get(exId);
      if (exSym === undefined) return;
      const cur = gen2symMap.get(sibId);
      if (cur === undefined) return;
      const exVal = Array.isArray(exSym) ? exSym[0] : exSym;
      if (!Array.isArray(cur)) {
        if (cur !== exVal) gen2symMap.set(sibId, [cur, exVal]);
      } else if (!cur.includes(exVal)) {
        cur.push(exVal);
      }
    });
  });

  // Schritt 5: BFS — Kinder erben Symbol von Eltern
  // Kinder von Expartnern erben das Symbol des Expartners
  const symQueue = [...gen2symMap.keys()];
  const symVisited = new Set(symQueue);
  while (symQueue.length) {
    const cur = symQueue.shift();
    const curSym = gen2symMap.get(cur);
    const symIdx = Array.isArray(curSym) ? curSym[0] : curSym;
    (childrenOf.get(cur) || []).forEach(child => {
      if (symVisited.has(child)) return;
      const otherParents = (parentsOf.get(child) || []).filter(p => p !== cur);
      const exParent = otherParents.find(p => (exPartnerOf.get(cur) || []).includes(p));

      if (exParent && gen2symMap.has(exParent)) {
        // Kind des Expartners erbt dessen Symbol
        const exSym = gen2symMap.get(exParent);
        gen2symMap.set(child, Array.isArray(exSym) ? exSym[0] : exSym);
      } else if (otherParents.length === 0 || !otherParents.some(p => gen2symMap.has(p))) {
        // Kind hat nur einen Gen2-Elternteil → erbt dessen Symbol direkt
        gen2symMap.set(child, symIdx);
      } else {
        gen2symMap.set(child, symIdx);
      }
      symVisited.add(child);
      symQueue.push(child);
    });
  }

  // Schritt 6: Gen2-Personen UND ihre Partner zeigen alle Symbole ihrer gemeinsamen Gen3-Kinder
  const gen2andPartners = new Set([...gen2set]);
  gen2set.forEach(id => {
    (currentPartnerOf.get(id) || []).forEach(pid => gen2andPartners.add(pid));
    (exPartnerOf.get(id) || []).forEach(pid => gen2andPartners.add(pid));
  });

  gen2andPartners.forEach(id => {
    if (!gen2symMap.has(id)) return;
    // Alle Kinder dieser Person + Kinder aller Partner
    const allPartners = [
      ...(currentPartnerOf.get(id) || []),
      ...(exPartnerOf.get(id) || []),
    ];
    const allChildren = new Set([
      ...(childrenOf.get(id) || []),
      ...allPartners.flatMap(pid => childrenOf.get(pid) || []),
    ]);
    const childSyms = new Set(
      [...allChildren]
        .filter(k => gen3set.has(k) && gen2symMap.has(k))
        .map(k => {
          const s = gen2symMap.get(k);
          return Array.isArray(s) ? s[0] : s;
        })
    );
    childSyms.forEach(s => {
      const cur = gen2symMap.get(id);
      if (!Array.isArray(cur)) {
        if (cur !== s) gen2symMap.set(id, [cur, s]);
      } else if (!cur.includes(s)) {
        cur.push(s);
      }
    });
  });

  // ── SYMBOLE für Gen1 (nach Gen2-Berechnung) ──────────────────────────
  // Gen1-Personen zeigen die echten Symbole ihrer direkten Gen2-Kinder.
  // Berechnung hier, damit gen2symMap bereits vollständig ist.
  gen1ids.forEach(id => {
    const myGen = genMap.get(id) ?? 0;
    const kids = (childrenOf.get(id) || []).filter(k => (genMap.get(k) ?? 0) > myGen);
    if (!kids.length) return;

    // Kinder nach Geburtsdatum sortieren für konsistente Reihenfolge
    const kidsSorted = [...kids].sort((a, b) => {
      const da = personById.get(a)?.birthDate || '';
      const db = personById.get(b)?.birthDate || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });

    // Symbole der Kinder aus gen2symMap lesen (dedupliziert, Reihenfolge beibehalten)
    const seen = new Set();
    const childSyms = [];
    kidsSorted.forEach(k => {
      const s = gen2symMap.get(k);
      const si = s !== undefined ? (Array.isArray(s) ? s[0] : s) : null;
      if (si !== null && !seen.has(si)) { seen.add(si); childSyms.push(si); }
    });

    if (childSyms.length > 0) {
      symbolMap.set(id, childSyms);
    } else {
      // Fallback: sequentiell (Kinder haben noch kein Gen2-Symbol)
      symbolMap.set(id, kids.map((_, i) => i % SYMBOLS.length));
    }
  });

  // ── OUTLINE-SYMBOLE für Gen3 ──────────────────────────────────────────
  // Gen3-Geschwister mit aktuellem Partner oder Expartner bekommen ein Outline-Symbol.
  // Zähler pro Elterngruppe, startet bei 0 (unabhängig von Gen2-Symbolen).
  const outlineSymMap = new Map();

  // Nur echte Gen3-Geschwister (haben Eltern in Gen2)
  const gen3sibs = gen3ids.filter(id =>
    (parentsOf.get(id) || []).some(p => gen2set.has(p))
  );

  // Sortiere nach Geburtsdatum
  const gen3sibsSorted = [...gen3sibs].sort((a, b) => {
    const da = personById.get(a)?.birthDate || '';
    const db = personById.get(b)?.birthDate || '';
    return da < db ? -1 : da > db ? 1 : 0;
  });

  // Pro Elterngruppe einen Zähler
  const outlineCounter = new Map(); // parentKey -> nextIdx

  gen3sibsSorted.forEach(sibId => {
    const hasPartner = (currentPartnerOf.get(sibId) || []).length > 0
                    || (exPartnerOf.get(sibId) || []).length > 0;
    if (!hasPartner) return;

    const parentKey = (parentsOf.get(sibId) || [])
      .filter(p => gen2set.has(p))
      .sort().join('|') || sibId;

    const nextSi = (outlineCounter.get(parentKey) ?? 0) % SYMBOLS.length;
    outlineCounter.set(parentKey, (outlineCounter.get(parentKey) ?? 0) + 1);

    const filledSym = gen2symMap.has(sibId)
      ? (Array.isArray(gen2symMap.get(sibId)) ? gen2symMap.get(sibId)[0] : gen2symMap.get(sibId))
      : undefined;

    // Geschwister bekommt Outline
    outlineSymMap.set(sibId, nextSi);

    // Aktuelle Partner: gleiches Outline + Filled-Symbol des Geschwister
    (currentPartnerOf.get(sibId) || []).forEach(pid => {
      outlineSymMap.set(pid, nextSi);
      if (!gen2symMap.has(pid) && filledSym !== undefined) gen2symMap.set(pid, filledSym);
    });

    // Expartner: gleiches Outline + Filled-Symbol des Geschwister
    (exPartnerOf.get(sibId) || []).forEach(exId => {
      outlineSymMap.set(exId, nextSi);
      if (!gen2symMap.has(exId) && filledSym !== undefined) gen2symMap.set(exId, filledSym);
    });
  });

  // Outline-Symbole an Gen4+ weitergeben
  gen3sibs.forEach(sibId => {
    const outSi = outlineSymMap.get(sibId);
    if (outSi === undefined) return;
    (childrenOf.get(sibId) || []).forEach(function propagate(child) {
      if (!outlineSymMap.has(child)) {
        outlineSymMap.set(child, outSi);
        (childrenOf.get(child) || []).forEach(propagate);
      }
    });
  });

  // ── ERGEBNIS ──────────────────────────────────────────────────────────
  const result = new Map();
  persons.forEach(p => {
    let ci = colorMap.get(p.id);
    const borders = borderColorsMap.get(p.id);
    const syms    = symbolMap.get(p.id);
    const sym2    = gen2symMap.get(p.id);
    const outSym  = outlineSymMap.get(p.id);
    // Falls keine eigene Farbe: von Elternteil erben
    if (ci === undefined && sym2 !== undefined) {
      for (const par of (parentsOf.get(p.id) || [])) {
        const parCi = colorMap.get(par);
        if (parCi !== undefined) { ci = parCi; break; }
      }
    }

    if (ci !== undefined || borders || syms || sym2 !== undefined) {
      const color = ci !== undefined ? FAMILY_COLORS[ci] : null;
      let symbols = null;
      if (syms) {
        symbols = syms.map(i => SYMBOLS[i]);
      } else if (Array.isArray(sym2)) {
        symbols = [...new Set(sym2)].map(i => SYMBOLS[i]);
      } else if (sym2 !== undefined) {
        symbols = [SYMBOLS[sym2]];
      }
      const outlineSymbol = outSym !== undefined ? SYMBOLS[outSym] : null;
      result.set(p.id, { color, borderColors: borders ? borders.map(i => FAMILY_COLORS[i]) : null, symbols, outlineSymbol, isGen1: gen1set.has(p.id) });
    }
  });
  return result;
}
